import {
  SMILE_SIMULATION_PROGRESS_SOFT_CAP,
  SMILE_SIMULATION_PROGRESS_TARGET,
} from "../model/constants";
import type {
  SmileSimulationProgressEasing,
  SmileSimulationProgressPlan,
  SmileSimulationProgressSegment,
} from "../model/types";

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const createSeededRandom = (seed: number) => {
  let nextSeed = seed >>> 0;

  return () => {
    nextSeed += 0x6d2b79f5;

    let result = Math.imul(nextSeed ^ (nextSeed >>> 15), 1 | nextSeed);
    result ^= result + Math.imul(result ^ (result >>> 7), 61 | result);

    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
};

const normalizeWeights = (weights: number[], total: number) => {
  const sum = weights.reduce((accumulator, weight) => accumulator + weight, 0);

  if (sum <= 0) {
    return weights.map(() => total / weights.length);
  }

  return weights.map((weight) => (weight / sum) * total);
};

const getRandomProgressPauseWeights = (count: number, random: () => number) => {
  const weights = Array.from({ length: count }, () => 0);
  const selectableIndices = Array.from({ length: count }, (_, index) => index);
  const pauseCount = Math.min(count, 3 + Math.floor(random() * 2));

  for (let iteration = 0; iteration < pauseCount; iteration += 1) {
    const nextIndex = Math.floor(random() * selectableIndices.length);
    const segmentIndex = selectableIndices.splice(nextIndex, 1)[0];

    if (segmentIndex == null) {
      continue;
    }

    weights[segmentIndex] = 0.8 + random() * 0.9;
  }

  return weights;
};

const applySmileSimulationProgressEasing = (
  value: number,
  easing: SmileSimulationProgressEasing,
) => {
  const clampedValue = clamp(value, 0, 1);

  if (easing === "linear") {
    return clampedValue;
  }

  if (easing === "easeOut") {
    return 1 - (1 - clampedValue) ** 3;
  }

  return -(Math.cos(Math.PI * clampedValue) - 1) / 2;
};

export const createSmileSimulationProgressPlan = (
  baseMs: number,
  seed = 1,
): SmileSimulationProgressPlan => {
  const random = createSeededRandom(seed);
  const segmentCount = 7;
  const pauseBudget = baseMs * clamp(0.1 + random() * 0.06, 0.08, 0.18);
  const moveBudget = Math.max(baseMs - pauseBudget, baseMs * 0.7);
  const moveWeights = normalizeWeights(
    [0.8, 1.05, 1.1, 0.95, 1.2, 1.05, 1.25].map(
      (weight) => weight * (0.88 + random() * 0.24),
    ),
    moveBudget,
  );
  const progressWeights = normalizeWeights(
    [0.08, 0.12, 0.11, 0.16, 0.13, 0.17, 0.23].map(
      (weight) => weight * (0.88 + random() * 0.24),
    ),
    SMILE_SIMULATION_PROGRESS_TARGET,
  );
  const pauseWeights = normalizeWeights(
    getRandomProgressPauseWeights(segmentCount - 1, random),
    pauseBudget,
  );
  const segments: SmileSimulationProgressSegment[] = [];
  let currentTime = 0;
  let currentProgress = 0;

  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
    const progressStep = progressWeights[segmentIndex] ?? 0;
    const moveDuration = moveWeights[segmentIndex] ?? 0;

    if (segmentIndex > 0) {
      const pauseDuration = pauseWeights[segmentIndex - 1] ?? 0;

      if (pauseDuration > 0) {
        segments.push({
          startMs: currentTime,
          endMs: currentTime + pauseDuration,
          startProgress: currentProgress,
          endProgress: currentProgress,
          easing: "linear",
        });
        currentTime += pauseDuration;
      }
    }

    const nextProgress =
      segmentIndex === segmentCount - 1
        ? SMILE_SIMULATION_PROGRESS_TARGET
        : currentProgress + progressStep;

    segments.push({
      startMs: currentTime,
      endMs: currentTime + moveDuration,
      startProgress: currentProgress,
      endProgress: nextProgress,
      easing: random() > 0.45 ? "easeOut" : "easeInOut",
    });

    currentTime += moveDuration;
    currentProgress = nextProgress;
  }

  const lastSegment = segments[segments.length - 1];

  if (lastSegment) {
    lastSegment.endMs = baseMs;
    lastSegment.endProgress = SMILE_SIMULATION_PROGRESS_TARGET;
  }

  return {
    baseMs,
    targetProgress: SMILE_SIMULATION_PROGRESS_TARGET,
    softCap: SMILE_SIMULATION_PROGRESS_SOFT_CAP,
    tailSpeed: 3.8 + random() * 0.8,
    segments,
  };
};

export const getSmileSimulationProgressAtElapsed = (
  elapsedMs: number,
  plan: SmileSimulationProgressPlan,
) => {
  if (elapsedMs <= 0) {
    return 0;
  }

  const activeSegment = plan.segments.find(
    (segment) => elapsedMs <= segment.endMs,
  );

  if (activeSegment) {
    if (
      activeSegment.endProgress === activeSegment.startProgress ||
      activeSegment.endMs === activeSegment.startMs
    ) {
      return activeSegment.endProgress;
    }

    const progressFraction =
      (elapsedMs - activeSegment.startMs) /
      (activeSegment.endMs - activeSegment.startMs);
    const easedFraction = applySmileSimulationProgressEasing(
      progressFraction,
      activeSegment.easing,
    );

    return clamp(
      activeSegment.startProgress +
        (activeSegment.endProgress - activeSegment.startProgress) *
          easedFraction,
      0,
      plan.targetProgress,
    );
  }

  const tailElapsed = elapsedMs - plan.baseMs;
  const tailFraction =
    1 - Math.exp((-plan.tailSpeed * tailElapsed) / Math.max(plan.baseMs, 1));

  return clamp(
    plan.targetProgress + (plan.softCap - plan.targetProgress) * tailFraction,
    plan.targetProgress,
    plan.softCap,
  );
};

export const getSmileSimulationCompletionProgress = (
  startProgress: number,
  elapsedMs: number,
  durationMs: number,
) => {
  if (durationMs <= 0) {
    return 100;
  }

  const easedFraction = 1 - (1 - clamp(elapsedMs / durationMs, 0, 1)) ** 4;

  return clamp(
    startProgress + (100 - startProgress) * easedFraction,
    startProgress,
    100,
  );
};

type SmileSimulationProgressLabels = {
  faceMapping: string;
  ratioCalculation: string;
  smileDesign: string;
  gumHarmony: string;
  textureRendering: string;
  completed: string;
};

const DEFAULT_SMILE_SIMULATION_PROGRESS_LABELS: SmileSimulationProgressLabels =
  {
    faceMapping: "안면 해부학 구조 및 골격 맵핑 중...",
    ratioCalculation: "입술 라인과 치아의 황금 비율 산출 중...",
    smileDesign: "스마일 아치와 정중선 정밀 설계 중...",
    gumHarmony: "치은 라인 및 주변부 심미 조화 매칭 중...",
    textureRendering: "자연광 반사를 고려한 치아 질감 렌더링 중...",
    completed: "당신만을 위한 새로운 미소 디자인 완료.",
  };

export const getSmileSimulationProgressLabel = (
  progress: number,
  labels = DEFAULT_SMILE_SIMULATION_PROGRESS_LABELS,
) => {
  if (progress >= 96) {
    return labels.completed;
  }

  if (progress >= 81) {
    return labels.textureRendering;
  }

  if (progress >= 61) {
    return labels.gumHarmony;
  }

  if (progress >= 41) {
    return labels.smileDesign;
  }

  if (progress >= 21) {
    return labels.ratioCalculation;
  }

  return labels.faceMapping;
};
